<template>
  <div class="select-filters-container">
    <div
      class="d-flex justify-space-between align-center"
      :class="{ 'mb-4': hasSelectedFilters, 'mb-0': !hasSelectedFilters }"
    >
      <div
        class="filters-header text-secondary font-weight-bold"
        :class="{ 'mb-2': !mdAndUp }"
      >
        Filters
      </div>
      <a
        v-if="hasSelectedFilters"
        class="reset-link text-primary"
        @click="props.resetSelections"
        role="button"
        tabindex="0"
        @keydown.enter.space.prevent="props.resetSelections"
        >Reset</a
      >
    </div>
    <div class="chips-outer-container">
      <transition
        name="fade-height"
        @before-leave="setInitialHeight"
        @leave="animateLeave"
      >
        <div
          v-if="hasSelectedFilters"
          class="chips-wrapper"
          :class="{ 'mb-4': !mdAndUp }"
        >
          <v-chip-group no-gutters column>
            <!-- Subject chips -->
            <v-chip
              v-for="subject in selectedSubjectsWithLabels"
              :key="subject.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="handleSubjectClose(subject.value)"
            >
              {{ subject.label }}
            </v-chip>

            <!-- Format chips -->
            <v-chip
              v-for="format in selectedFormatsWithLabels"
              :key="format.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="handleFormatClose(format.value)"
            >
              {{ format.label }}
            </v-chip>

            <!-- Grade chips -->
            <v-chip
              v-for="grade in selectedGradesWithLabels"
              :key="grade.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="handleGradeClose(grade.value)"
            >
              {{ grade.label }}
            </v-chip>

            <!-- Alignment chips -->
            <v-chip
              v-for="alignment in selectedAlignmentWithLabels"
              :key="alignment.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="handleAlignmentClose(alignment.value)"
            >
              {{ alignment.label }}
            </v-chip>

            <!-- Usability chips -->
            <v-chip
              v-for="usability in selectedUsabilityWithLabels"
              :key="usability.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="handleUsabilityClose(usability.value)"
            >
              {{ usability.label }}
            </v-chip>

            <!-- Version chips -->
            <v-chip
              v-for="version in selectedVersionsWithLabels"
              :key="version.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="handleVersionClose(version.value)"
            >
              {{ version.label }}
            </v-chip>

            <!-- Publisher chips -->
            <v-chip
              v-for="publisher in selectedPublishersWithLabels"
              :key="publisher.value"
              closable
              color="primary"
              class="custom-chip"
              close-icon="mdi-close"
              variant="flat"
              @click:close="handlePublisherClose(publisher.value)"
            >
              {{ publisher.label }}
            </v-chip>
          </v-chip-group>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useReportsStore } from '../../store';
  import { useDisplay } from 'vuetify';
  import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue';
  import { useRouter } from 'vue-router';

  const props = defineProps<{
    resetSelections: () => void;
    class?: string;
  }>();

  const reportsStore = useReportsStore();
  const display = useDisplay();
  const router = useRouter();
  const mdAndUp = computed(() => display.mdAndUp.value);
  const observer = ref<ResizeObserver | null>(null);

  const hasSelectedFilters = computed(
    () =>
      reportsStore.selectedSubjects.length > 0 ||
      reportsStore.selectedFormats.length > 0 ||
      reportsStore.selectedGrades.length > 0 ||
      reportsStore.selectedAlignment.length > 0 ||
      reportsStore.selectedUsability.length > 0 ||
      reportsStore.selectedVersions.length > 0 ||
      (reportsStore.selectedPublishers &&
        reportsStore.selectedPublishers.length > 0),
  );

  onMounted(async () => {
    await reportsStore.fetchFilters();

    observer.value = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const chip = entry.target as HTMLElement;
        const content = chip.querySelector('.v-chip__content') as HTMLElement;
        if (content) {
          const textSpan = content.firstElementChild as HTMLElement;
          if (textSpan) {
            const hasOverflow = textSpan.scrollWidth > content.clientWidth;
            chip.classList.toggle('has-overflow', hasOverflow);
          }
        }
      });
    });

    // Wait for chips to be rendered
    setTimeout(() => {
      document.querySelectorAll('.custom-chip').forEach((chip) => {
        if (observer.value) {
          observer.value.observe(chip);
        }
      });
    }, 100);
  });

  // Properly placed outside of onMounted
  onUnmounted(() => {
    if (observer.value) {
      observer.value.disconnect();
    }
  });

  // Animation helpers
  function setInitialHeight(element: Element) {
    const height = (element as HTMLElement).offsetHeight;
    (element as HTMLElement).style.height = height + 'px';
  }

  function animateLeave(element: Element, done: () => void) {
    requestAnimationFrame(() => {
      (element as HTMLElement).style.height = '0';
      (element as HTMLElement).style.opacity = '0';
      element.addEventListener('transitionend', done, { once: true });
    });
  }

  // Helper function to find the label for a filter value
  const getFilterLabel = (filterId: string, value: string): string => {
    const filter = reportsStore.filters.find(
      (f) => f.id === filterId || f.slug === filterId,
    );
    if (!filter) return value;

    const option = filter.options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  // Computed properties for selected filters with proper labels
  const selectedSubjectsWithLabels = computed(() =>
    reportsStore.selectedSubjects.map((subject) => ({
      value: subject,
      label: getFilterLabel('subject', subject),
    })),
  );

  const selectedFormatsWithLabels = computed(() =>
    reportsStore.selectedFormats.map((format) => ({
      value: format,
      label: getFilterLabel('formats', format),
    })),
  );

  const selectedGradesWithLabels = computed(() =>
    reportsStore.selectedGrades.map((grade) => ({
      value: grade,
      label: getFilterLabel('grade-levels', grade),
    })),
  );

  const selectedAlignmentWithLabels = computed(() =>
    reportsStore.selectedAlignment.map((alignment) => ({
      value: alignment,
      label: getFilterLabel('alignment', alignment),
    })),
  );

  const selectedUsabilityWithLabels = computed(() =>
    reportsStore.selectedUsability.map((usability) => ({
      value: usability,
      label: getFilterLabel('usability', usability),
    })),
  );

  const selectedVersionsWithLabels = computed(() =>
    reportsStore.selectedVersions.map((version) => ({
      value: version,
      label: getFilterLabel('versions', version),
    })),
  );

  const selectedPublishersWithLabels = computed(() =>
    (reportsStore.selectedPublishers || []).map((publisher) => ({
      value: publisher,
      label: getFilterLabel('publishers', publisher),
    })),
  );

  // Debounce utility for route updates
  const useDebounceFn = (fn: (...args: any[]) => void, delay: number) => {
    let timeout: number | null = null;
    return (...args: any[]) => {
      if (timeout !== null) clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        fn(...args);
        timeout = null;
      }, delay);
    };
  };

  // Route update logic
  const updateRoute = useDebounceFn(() => {
    const query: Record<string, string[]> = {};
    const subject = reportsStore.selectedSubjects[0];
    if (reportsStore.selectedFormats.length > 0)
      query.formats = reportsStore.selectedFormats;
    if (reportsStore.selectedGrades.length > 0)
      query.grades = reportsStore.selectedGrades;
    if (reportsStore.selectedAlignment.length > 0)
      query.alignment = reportsStore.selectedAlignment;
    if (reportsStore.selectedUsability.length > 0)
      query.usability = reportsStore.selectedUsability;
    if (reportsStore.selectedVersions.length > 0)
      query.versions = reportsStore.selectedVersions;
    if (
      reportsStore.selectedPublishers &&
      reportsStore.selectedPublishers.length > 0
    )
      query.publishers = reportsStore.selectedPublishers;
    if (
      reportsStore.selectedSupportingDocuments &&
      reportsStore.selectedSupportingDocuments.length > 0
    )
      query.supportingDocuments = reportsStore.selectedSupportingDocuments;
    const path = subject ? `/reports/${subject.toLowerCase()}` : '/reports';
    router.replace({ path, query });
  }, 300);

  // Enhanced chip close handlers that update the route
  const handleSubjectClose = async (subjectValue: string) => {
    reportsStore.setSubject(subjectValue, false);
    await nextTick();
    updateRoute();
  };

  const handleFormatClose = async (formatValue: string) => {
    reportsStore.setFormat(formatValue, false);
    await nextTick();
    updateRoute();
  };

  const handleGradeClose = async (gradeValue: string) => {
    reportsStore.setGrade(gradeValue, false);
    await nextTick();
    updateRoute();
  };

  const handleAlignmentClose = async (alignmentValue: string) => {
    reportsStore.setAlignment(alignmentValue, false);
    await nextTick();
    updateRoute();
  };

  const handleUsabilityClose = async (usabilityValue: string) => {
    reportsStore.setUsability(usabilityValue, false);
    await nextTick();
    updateRoute();
  };

  const handleVersionClose = async (versionValue: string) => {
    reportsStore.setVersion(versionValue, false);
    await nextTick();
    updateRoute();
  };

  const handlePublisherClose = async (publisherValue: string) => {
    reportsStore.setPublisher?.(publisherValue, false);
    await nextTick();
    updateRoute();
  };
</script>

<style lang="scss" scoped>
  .v3-reports-sidebar {
    .select-filters-container {
      overflow: hidden;
    }

    .chips-outer-container {
      position: relative;
    }

    .chips-wrapper {
      width: 100%;
    }

    .custom-chip {
      border-radius: 4px !important;
      height: 32px !important;
      font-size: 12px !important;
      background-color: rgb(var(--v-theme-primary)) !important;
      color: white !important;
      max-width: 200px !important;
      position: relative !important;

      :deep(.v-chip__content) {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        position: relative !important;
      }

      &.has-overflow :deep(.v-chip__content) {
        padding-right: 32px !important;

        &::after {
          content: '' !important;
          position: absolute !important;
          right: 0 !important;
          top: 0 !important;
          height: 100% !important;
          width: 32px !important;
          background: linear-gradient(
            to right,
            transparent,
            rgb(var(--v-theme-primary))
          ) !important;
          pointer-events: none !important;
        }
      }

      :deep(.v-chip__close) {
        color: white !important;
        opacity: 1 !important;
        position: relative !important;
        z-index: 1 !important;
      }
    }

    .reset-link {
      cursor: pointer;
      text-decoration: underline;
      margin-top: 0;
      display: inline-block;
    }

    // Fade and height transition
    .fade-height-enter-active,
    .fade-height-leave-active {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      overflow: hidden;
    }

    .fade-height-enter-from,
    .fade-height-leave-to {
      opacity: 0;
      height: 0;
    }
  }
</style>
